import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL?.endsWith("/api") ? BACKEND_URL : `${BACKEND_URL}/api`;

/**
 * Leagues hold an evolving club/series roster.
 * Matches can link to a league and snapshot that roster for match day.
 * Shooters themselves always live in the global directory.
 */
const LeagueList = () => {
  const { isAdmin } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ name: "", season: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [roster, setRoster] = useState(null);
  const [allShooters, setAllShooters] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3500);
  };

  const loadLeagues = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leagues`);
      setLeagues(res.data || []);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Failed to load leagues");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeagues();
  }, [loadLeagues]);

  const loadRoster = async (leagueId) => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/leagues/${leagueId}/roster`),
        axios.get(`${API}/shooters`),
      ]);
      setRoster(r.data);
      setAllShooters(s.data || []);
      setSelectedIds([]);
      setNewName("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load league roster");
    }
  };

  const toggleExpand = async (leagueId) => {
    if (expandedId === leagueId) {
      setExpandedId(null);
      setRoster(null);
      return;
    }
    setExpandedId(leagueId);
    await loadRoster(leagueId);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("League name is required");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await axios.post(`${API}/leagues`, {
        name: form.name.trim(),
        season: form.season.trim() || null,
        description: form.description.trim() || null,
      });
      setForm({ name: "", season: "", description: "" });
      showSuccess("League created");
      await loadLeagues();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create league");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteLeague = async (league) => {
    if (
      !window.confirm(
        `Delete league "${league.name}"?\n\n` +
          "Shooters, matches, and scores are kept. Linked matches are unlinked only."
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API}/leagues/${league.id}`);
      if (expandedId === league.id) {
        setExpandedId(null);
        setRoster(null);
      }
      showSuccess("League deleted");
      await loadLeagues();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete league");
    }
  };

  const rosterIdSet = new Set((roster?.members || []).map((m) => m.id));
  const available = allShooters.filter((s) => !rosterIdSet.has(s.id));

  const handleAddToRoster = async () => {
    if (!expandedId) return;
    if (selectedIds.length === 0 && !newName.trim()) {
      setError("Select shooters or enter a new name");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/leagues/${expandedId}/roster`, {
        shooter_ids: selectedIds,
        new_shooters: newName.trim() ? [{ name: newName.trim() }] : [],
      });
      setRoster(res.data);
      setSelectedIds([]);
      setNewName("");
      const shootersRes = await axios.get(`${API}/shooters`);
      setAllShooters(shootersRes.data || []);
      await loadLeagues();
      showSuccess("League roster updated");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update league roster");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveFromRoster = async (shooter) => {
    if (!expandedId) return;
    if (
      !window.confirm(
        `Remove "${shooter.name}" from this league roster?\n\n` +
          "Their profile stays. Existing match rosters and scores are unchanged."
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await axios.delete(
        `${API}/leagues/${expandedId}/roster/${shooter.id}`
      );
      await loadRoster(expandedId);
      await loadLeagues();
      showSuccess(`Removed ${shooter.name} from league`);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove from league");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page text-center text-ink-500">Loading leagues...</div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title mb-2">Leagues</h1>
      <p className="page-subtitle mb-6 max-w-3xl">
        A <strong>league</strong> is a club or series with a roster that grows over
        the season. Each <strong>match</strong> keeps its own day-of roster
        (snapshot). Create a match linked to a league to seed that day&apos;s
        roster; pull new league members later or promote match guests up to the
        league.
      </p>

      {error && (
        <div className="alert-error mb-4" role="alert">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}
      {success && (
        <div className="alert-success mb-4" role="status">
          {success}
        </div>
      )}

      {isAdmin() && (
        <div className="card card-pad mb-6">
          <h2 className="section-title mb-4">Create League</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="field-label">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="e.g. Capital City Pistol League"
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="field-label">
                  Season (optional)
                </label>
                <input
                  type="text"
                  value={form.season}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, season: e.target.value }))
                  }
                  placeholder="e.g. 2026 Outdoor"
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  className="input"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={creating}
                className="btn-primary"
              >
                {creating ? "Creating..." : "Create League"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {leagues.length === 0 ? (
          <div className="card card-pad text-center text-ink-500">
            No leagues yet. Create one to hold a season roster shared across matches.
          </div>
        ) : (
          leagues.map((league) => (
            <div key={league.id} className="card overflow-hidden">
              <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-ink-900">{league.name}</h3>
                  <p className="text-sm text-ink-500">
                    {league.season || "No season label"} ·{" "}
                    {(league.roster_shooter_ids || []).length} on roster
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => toggleExpand(league.id)}
                    className="btn-secondary btn-sm"
                  >
                    {expandedId === league.id ? "Hide roster" : "Manage roster"}
                  </button>
                  {isAdmin() && (
                    <button
                      type="button"
                      onClick={() => handleDeleteLeague(league)}
                      className="btn-ghost btn-sm text-rose-600 hover:text-rose-700"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {expandedId === league.id && roster && (
                <div className="border-t border-ink-100 px-4 py-4 bg-ink-50 space-y-4">
                  <p className="text-sm text-ink-600">
                    Linked matches: {roster.match_count}. Adding here does not
                    change past match rosters until you sync a match.
                  </p>

                  {(roster.members || []).length === 0 ? (
                    <p className="text-sm text-ink-500">League roster is empty.</p>
                  ) : (
                    <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>NRA</th>
                          <th>Rating</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roster.members.map((s) => (
                          <tr key={s.id}>
                            <td>
                              <Link
                                to={`/shooters/${s.id}`}
                                className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                              >
                                {s.name}
                              </Link>
                            </td>
                            <td className="text-ink-500">
                              {s.nra_number || "—"}
                            </td>
                            <td className="text-ink-500">
                              {s.rating || "—"}
                            </td>
                            <td className="text-right">
                              {isAdmin() && (
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => handleRemoveFromRoster(s)}
                                  className="text-rose-600 hover:text-rose-700 hover:underline disabled:opacity-50"
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}

                  {isAdmin() && (
                    <div className="space-y-3 border-t border-ink-100 pt-3">
                      <h4 className="font-medium text-sm text-ink-800">Add to league roster</h4>
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="New shooter full name"
                        className="input md:w-1/2"
                      />
                      {available.length > 0 && (
                        <div className="max-h-40 overflow-y-auto border border-ink-200 rounded-lg p-2 bg-white space-y-1">
                          {available.map((s) => (
                            <label
                              key={s.id}
                              className="flex items-center gap-2 text-sm text-ink-700 px-1 py-0.5 rounded hover:bg-ink-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(s.id)}
                                onChange={() =>
                                  setSelectedIds((prev) =>
                                    prev.includes(s.id)
                                      ? prev.filter((x) => x !== s.id)
                                      : [...prev, s.id]
                                  )
                                }
                              />
                              {s.name}
                            </label>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={handleAddToRoster}
                        className="btn-primary btn-sm"
                      >
                        {busy ? "Saving..." : "Add to League"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LeagueList;
